import { useState, useCallback, useMemo, type ReactNode } from 'react';
import type { CustomizationService } from '../../application/services/CustomizationService';
import type { ProductCustomization } from '../../domain/models/ProductCustomization';
import type { SizeOption } from '../../utils/parseSizeList';
import { useCustomizationSheet } from '../hooks/useCustomizationSheet';
import { CustomizationSheet } from './CustomizationSheet';

export interface ProductCustomizationContext {
  designId: string;
  designName: string;
  category: string;
  customizationOptions: unknown;
  metal: string;
  goldPurity: string | null;
  diamondStoneType: string | null;
  solitaireCarat: number | null;
  solitaireClarity: string | null;
  solitaireColor: string | null;
  customizationId: number;
  size: string | null;
  discountedPrice: number | null;
  isSizeable: boolean;
  nddAvailableSizes: string[] | null;
}

interface ProductCustomizationProps {
  service: CustomizationService;
  context: ProductCustomizationContext | null;
  selectedSize: SizeOption | null;
  onApplied: (result: {
    customization: ProductCustomization;
    selectedSize: SizeOption | null;
  }) => void;
  onCustomizeOpen?: () => void;
  onCustomizationSelected?: (
    customizationType: string,
    appliedCustomization?: ProductCustomization,
  ) => void;
  children: (openSheet: () => void) => ReactNode;
}

export function ProductCustomization({
  service,
  context,
  selectedSize,
  onApplied,
  onCustomizeOpen,
  onCustomizationSelected,
  children,
}: ProductCustomizationProps) {
  const [isOpen, setIsOpen] = useState(false);

  const sheetContext = useMemo(
    () =>
      context
        ? {
            designId: context.designId,
            designName: context.designName,
            customizationOptions: context.customizationOptions,
            metal: context.metal,
            goldPurity: context.goldPurity,
            diamondStoneType: context.diamondStoneType,
            solitaireCarat: context.solitaireCarat,
            solitaireClarity: context.solitaireClarity,
            solitaireColor: context.solitaireColor,
            customizationId: context.customizationId,
            size: context.size,
            discountedPrice: context.discountedPrice,
          }
        : null,
    [context]
  );

  const handleClose = useCallback(() => setIsOpen(false), []);
  const openSheet = useCallback(() => {
    onCustomizeOpen?.();
    setIsOpen(true);
  }, [onCustomizeOpen]);

  const sheet = useCustomizationSheet({
    service,
    context: sheetContext,
    selectedSize,
    isOpen,
    onClose: handleClose,
  });

  const handleApply = useCallback(async () => {
    const result = await sheet.handleApply();
    if (result) {
      onApplied(result);
      onCustomizationSelected?.('Apply', result.customization);
    }
  }, [onApplied, onCustomizationSelected, sheet]);

  if (!context) return <>{children(openSheet)}</>;

  return (
    <>
      {children(openSheet)}
      <CustomizationSheet
        open={isOpen}
        designName={context.designName}
        draft={sheet.draft}
        parsedOptions={sheet.parsedOptions}
        goldPurities={sheet.goldPurities}
        previewPrice={sheet.previewPrice}
        isPreviewLoading={sheet.isPreviewLoading}
        isPriceAnimating={sheet.isPriceAnimating}
        isApplying={sheet.isApplying}
        onClose={handleClose}
        onOptionChange={sheet.handleOptionChange}
        onApply={handleApply}
        onCancel={sheet.handleCancel}
      />
    </>
  );
}
